import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';

import type { EquipmentListItemVM } from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';
import type TimeSplitEquipmentVM from 'ab-viewModels/timeSplitEquipment/timeSplitEquipment';

import type * as CustomerSignatureRequestModel from 'ab-requestModels/signature.requestModel';

import type { BlockValueType, EquipmentFieldVM, ImmutableUrlFieldVM, RepeatableBlockValueType, SignatureFieldVM, ValueType } from '@acceligentllc/shared/utils/fieldReport';
import { getUserName } from '@acceligentllc/shared/utils/user';

import { ColorHex, ExtendedColorPalette } from '@acceligentllc/shared/enums/color';
import { CompoundUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';
import { ReportBlockFieldIconName } from '@acceligentllc/shared/enums/reportBlockFieldIcon';
import { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';

import type { RootState } from 'af-reducers';

import { debounce } from 'af-utils/actions.util';
import { bemElement } from 'af-utils/bem.util';

import { FIELD_REPORT } from 'af-constants/reduxForms';
import { INPUT_FIELD_MAX_CHARACTERS, INPUT_FIELD_MAX_DIGITS } from 'af-constants/values';

import * as FieldReportActions from 'af-actions/fieldReport';

import AddressField from './Address';
import BooleanField from './Boolean';
import CalculatedField from './Calculated';
import CompoundField from './Compound';
import DropdownField from './Dropdown';
import EquipmentDropdown from './EquipmentDropdown';
import ImageField from './Image';
import ImmutableUrl from './ImmutableUrl';
import InputField from './Input';
import SignatureField from './Signature';
import TextAreaField from './TextArea';
import TimeInputField from './Time';
import type { RequestQueue } from '../../../helpers';

interface SharedProps {
	fieldReportId: number;
	fieldReportTypeId: number;
	fieldId: string;
	blockId: string;
	disabled: boolean;
	fieldName: string;
	focusedBlockId: Nullable<number>;
	setFieldToFocus: (ref: HTMLDivElement) => void;
	removeFocusedField: () => void;
}

interface PreviewProps extends SharedProps {
	isPreview: true;
	onFocus?: () => void;
	change?: (fieldName: string, value: BlockValueType) => void;
}

interface EditableProps extends SharedProps {
	isPreview: false;
	onFocus?: () => void;
	change: (fieldName: string, value: BlockValueType) => void;
}

type OwnProps = PreviewProps | EditableProps;

type QueueProps = {
	requestQueue?: RequestQueue;
};

type Props = OwnProps & QueueProps & ConnectedProps<typeof connector>;

interface State {
	showSignatureModal: boolean;
	signatureValue: Nullable<{ signedAt: string; signature: string; fullName: string; }>;
	isBlurableChanged: boolean;
	equipmentList: string[];
}

class Field extends React.PureComponent<Props> {

	state: State = {
		showSignatureModal: false,
		signatureValue: null,
		isBlurableChanged: false,
		equipmentList: [],
	};

	saveValue = (newValue: BlockValueType) => {
		const {
			fieldName,
			fieldId,
			fieldReportBlockField,
			updateFieldValue,
			fieldReportTypeId,
			fieldReportId,
			blockId,
			requestQueue,
		} = this.props;
		const { value } = fieldReportBlockField;
		const isArray = Array.isArray(value);

		if (requestQueue) {
			const data = {
				fieldId,
				value: newValue,
				index: isArray ? +fieldName.split('field-')[1].split(/\[(.*?)\]/g)[1] : null,
				blockId,
			};

			requestQueue.add(async () => {
				await updateFieldValue(fieldReportId, fieldReportTypeId, data);
				this.syncRequiredFieldFilledValue(fieldId, newValue);
			});
		}
	};

	syncRequiredFieldFilledValue = (fieldId: string, newValue: ValueType) => {
		const { fieldReport, syncFieldReportTypes } = this.props;

		const field = fieldReport?.fieldReportBlockFieldMap[fieldId];
		const block = fieldReport?.fieldReportBlockMap[field?.blockId ?? -1];
		const fieldReportType = fieldReport?.typeMap[block?.fieldReportTypeId ?? -1];
		if (!field || !block || !fieldReportType) {
			return;
		}

		const fieldDefinition = fieldReport?.fieldMap[field?.reportBlockFieldId];

		if (!fieldDefinition?.isRequired) {
			return;
		}

		const _hasValue = !(
			newValue === null
			|| newValue === ''
			|| (Array.isArray(newValue) && (newValue as RepeatableBlockValueType).length === 0)
			|| (Array.isArray(newValue) && (newValue as RepeatableBlockValueType).length === 1 && newValue[1] === null)
			|| (Array.isArray(newValue) && (newValue as RepeatableBlockValueType).length === 1 && newValue[1] === '')
			|| (fieldDefinition.fieldType === ReportBlockFieldEnum.Type.SIGNATURE && (newValue as SignatureFieldVM).id === -1)
			|| (fieldDefinition.fieldType === ReportBlockFieldEnum.Type.EQUIPMENT && (newValue as EquipmentFieldVM).id === null)
			|| (fieldDefinition.fieldType === ReportBlockFieldEnum.Type.IMMUTABLE_URL && (newValue as ImmutableUrlFieldVM).displayPath === null)
		);

		const currentCount = fieldReport.typeMap[fieldReportType.id].numberOfFilledRequiredFields;
		const maxCount = fieldReport.typeMap[fieldReportType.id].numberOfRequiredFields;
		const newCountValue = _hasValue ? Math.min(currentCount + 1, maxCount) : Math.max(0, (currentCount - 1));
		const newFieldReportMap = {
			...fieldReport.typeMap,
			[fieldReportType.id]: {
				...fieldReport.typeMap[fieldReportType.id],
				numberOfFilledRequiredFields: Math.max(0, newCountValue),
			},
		};
		// this need rethinking. There is possibility that one change will trigger this sync twice.
		// at the moment when this was implemented, only one field was required, so I didn't go into too much
		syncFieldReportTypes({
			fieldReport: {
				...fieldReport,
				typeMap: newFieldReportMap,
			},
		});
	};

	changeAndSave = (newValue: BlockValueType) => {
		const {
			fieldName,
			change,
			reportBlockField,
		} = this.props;
		change?.(fieldName, newValue);

		if (reportBlockField.fieldType === ReportBlockFieldEnum.Type.EQUIPMENT && !newValue) {
			this.saveValue({ id: null });
		} else {
			this.saveValue(newValue);
		}
	};

	autosave = () => {
		const { formValue } = this.props;
		this.saveValue(formValue ?? '');
	};

	autosaveBlurable = () => {
		if (this.state.isBlurableChanged) {
			const { formValue } = this.props;
			this.saveValue(formValue ?? '');
			this.clearBlurableChanged();
		}
	};

	// eslint-disable-next-line @typescript-eslint/member-ordering
	autosaveDebounced = debounce(this.autosave, 300);

	focusOnChange = () => {
		this.onFieldFocus();
		this.autosaveDebounced();
	};

	openSignatureModal = () => {
		this.setState(() => ({ showSignatureModal: true }));
	};

	closeSignatureModal = () => {
		this.setState(() => ({ showSignatureModal: false }));
	};

	setSignatureValue = async (signature: CustomerSignatureRequestModel.SignatureForm) => {
		this.setState(() => ({ signatureValue: { signedAt: signature.signedAt, signature: signature.signatureImage, fullName: signature.fullName } }));
	};

	setEquipmentList = async (equipmentList: TimeSplitEquipmentVM) => {
		this.setState(() => ({ equipmentList: this.getSections(equipmentList) }));
	};

	setBlurableChanged = () => {
		this.setState(() => ({ isBlurableChanged: true }));
	};

	clearBlurableChanged = () => {
		this.setState(() => ({ isBlurableChanged: false }));
	};

	onDeleteSignature = async (signature: SignatureFieldVM) => {
		const { deleteSignature } = this.props;
		const form = {
			signatureId: signature.id,
		};
		await deleteSignature(form);
		const newValue = {
			id: -1,
			signedAt: '',
			imageUrl: '',
			name: '',
		};
		this.changeAndSave(newValue);
	};

	onFieldFocus = () => {
		const { onFocus, focusedBlockId, removeFocusedField } = this.props;
		onFocus?.();

		if (focusedBlockId) {
			removeFocusedField();
		}
	};

	getSections = (equipmentOptions: TimeSplitEquipmentVM) => {
		if (!equipmentOptions) {
			return null;
		}
		return [
			{
				title: 'Suggested',
				options: equipmentOptions?.suggested,
			},
			{
				title: 'All',
				options: equipmentOptions?.all,
			},
		];
	};

	onEquipmentFieldFocus = async () => {
		const { onFocus, focusedBlockId, fieldReportId, removeFocusedField, fetchEquipmentList } = this.props;
		const { equipmentList } = this.state;
		onFocus?.();

		if (!equipmentList.length) {
			const fetchedEquipment = await fetchEquipmentList(fieldReportId);
			this.setEquipmentList({
				all: fetchedEquipment.all.filter((e) => e.status !== ResourceStatus.DELETED),
				suggested: fetchedEquipment.suggested.filter((e) => e.status !== ResourceStatus.DELETED),
			});
		}

		if (focusedBlockId) {
			removeFocusedField();
		}
	};

	renderImageField = () => {
		const {
			disabled,
			isPreview,
			reportBlockField: { name, tooltipText, hasTooltip, fieldType },
			fieldName,
			fieldId,
			fieldReportId,
			fieldReportTypeId,
			blockId,
		} = this.props;

		return (
			<ImageField
				blockId={+blockId}
				disabled={disabled || isPreview}
				fieldId={+fieldId}
				fieldReportId={fieldReportId}
				fieldReportTypeId={fieldReportTypeId}
				formName={fieldName}
				isEditable={!disabled && !isPreview && fieldType !== ReportBlockFieldEnum.Type.IMMUTABLE_IMAGE}
				name={name}
				onFieldFocus={this.onFieldFocus}
				onSave={this.changeAndSave}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderBooleanField = () => {
		const { disabled, isPreview, reportBlockField: { name, tooltipText, hasTooltip }, fieldName } = this.props;

		return (
			<BooleanField
				disabled={disabled}
				formName={fieldName}
				isPreview={isPreview}
				name={name}
				onValueChange={this.focusOnChange}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderAddressField = () => {
		const { disabled, isPreview, reportBlockField: { name, tooltipText, hasTooltip }, fieldName, change } = this.props;

		return (
			<AddressField
				change={change}
				disabled={disabled}
				formName={fieldName}
				isPreview={isPreview}
				name={name}
				onFocus={this.onFieldFocus}
				onValueChange={this.autosaveDebounced}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderSignatureField = () => {
		const {
			disabled,
			isPreview,
			reportBlockField: { name },
			formValue,
			fieldReportId,
			fieldName,
		} = this.props;

		return (
			<SignatureField
				deleteSignature={this.onDeleteSignature}
				disabled={disabled}
				fieldReportId={fieldReportId}
				formName={fieldName}
				isPreview={isPreview}
				name={name}
				onFocus={this.onFieldFocus}
				onValueChange={this.changeAndSave}
				value={formValue as SignatureFieldVM}
			/>
		);
	};

	renderTextAreaField = () => {
		const { disabled, isPreview, reportBlockField: { name, tooltipText, hasTooltip, textLimit }, fieldName } = this.props;

		return (
			<TextAreaField
				disabled={disabled}
				formName={fieldName}
				isPreview={isPreview}
				maxCharacters={textLimit ?? INPUT_FIELD_MAX_CHARACTERS}
				name={name}
				onBlur={this.autosaveBlurable}
				onChange={this.setBlurableChanged}
				onFocus={this.onFieldFocus}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderCompoundField = () => {
		const { disabled, isPreview, reportBlockField: { unit, name, tooltipText, hasTooltip }, fieldName } = this.props;

		return (
			<CompoundField
				disabled={disabled}
				formName={fieldName}
				isPreview={isPreview}
				maxDigits={INPUT_FIELD_MAX_DIGITS}
				name={name}
				onBlur={this.autosaveDebounced}
				onFocus={this.onFieldFocus}
				onValueChange={this.setBlurableChanged}
				tooltipMessage={hasTooltip ? tooltipText : null}
				unit={unit as CompoundUnitEnum}
			/>
		);
	};

	renderTimeInputField = () => {
		const { disabled, isPreview, reportBlockField: { name, tooltipText, hasTooltip }, fieldName } = this.props;

		return (
			<TimeInputField
				disabled={disabled}
				formName={fieldName}
				isPreview={isPreview}
				name={name}
				onFocus={this.onFieldFocus}
				onValueChange={this.autosaveDebounced}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderPlainField = () => {
		const { disabled, isPreview, reportBlockField, fieldName } = this.props;
		const { name, unit, valueType, tooltipText, hasTooltip, fieldType } = reportBlockField;

		return (
			<InputField
				disabled={disabled}
				formName={fieldName}
				hasTooltip={hasTooltip}
				isPreview={isPreview || fieldType === 'IMMUTABLE_TEXT'}
				maxDigits={fieldType === 'NUMERIC_ATTRIBUTE' ? INPUT_FIELD_MAX_DIGITS : null}
				name={name}
				onBlur={this.autosaveBlurable}
				onChange={this.setBlurableChanged}
				onFocus={this.onFieldFocus}
				tooltipMessage={tooltipText}
				unit={unit}
				valueType={valueType}
			/>
		);
	};

	filterEquipment = (option: EquipmentListItemVM, searchText: string) => {
		const equipmentText = (`${option.code} ${option.specification}`).toLocaleLowerCase();
		return equipmentText.includes(searchText.toLocaleLowerCase());
	};

	renderEquipment = (entry: EquipmentListItemVM) => {
		return (
			<div className="equipment">
				<span className="condensed-table__employee-list-equipment-row__equipment-color" style={{ backgroundColor: ColorHex[entry.color] }} />
				<span>{entry.code ?? ''}</span>
				{!!entry.specification && (
					<span className="condensed-table__employee-list-equipment-row__specification">({entry.specification})</span>
				)}
			</div>
		);
	};

	renderEquipmentDropdownField = () => {
		const { reportBlockField, fieldName, formValue, isPreview, disabled } = this.props;
		const { name, hasTooltip, tooltipText } = reportBlockField;
		const { equipmentList } = this.state;

		const equipmentObj: EquipmentListItemVM = typeof (formValue) === 'string' ? JSON.parse(formValue) : formValue;
		const readonlyEquipmentText = (equipmentObj?.id) ? `${equipmentObj.code ?? ''} (${equipmentObj.specification ?? ''})` : undefined;
		return (
			<EquipmentDropdown
				disabled={disabled}
				filterBy={this.filterEquipment}
				formName={fieldName}
				isPreview={isPreview}
				label={name}
				name={fieldName}
				onFocus={this.onEquipmentFieldFocus}
				onValueChange={this.changeAndSave}
				readonlyEquipmentText={readonlyEquipmentText}
				renderMenuItem={this.renderEquipment}
				sections={equipmentList}
				selected={equipmentObj ? equipmentObj.id : null}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderDropdownField = () => {
		const { disabled, reportBlockField, fieldName, formValue, change, isPreview } = this.props;
		const { name, options, allowCustomDropdownListValue, hasTooltip, tooltipText } = reportBlockField;

		return (
			<DropdownField
				allowCustomValue={allowCustomDropdownListValue}
				change={change}
				disabled={disabled}
				formName={fieldName}
				initialValue={formValue as string}
				isPreview={isPreview}
				name={name}
				onFocus={this.onFieldFocus}
				onValueChange={this.autosaveDebounced}
				options={options ?? []}
				tooltipMessage={hasTooltip ? tooltipText : null}
			/>
		);
	};

	renderCalculatedField = () => {
		const { reportBlockField: { name, tooltipText, hasTooltip, unit }, fieldName } = this.props;

		return (
			<CalculatedField
				formName={fieldName}
				name={name}
				tooltipMessage={hasTooltip ? tooltipText : null}
				unit={unit}
			/>
		);
	};

	renderImmutableTextField = () => {
		const {
			reportBlockField: {
				defaultValue,
				isDescriptiveTextBold,
				descriptiveTextColor,
			},
		} = this.props;

		const blackText = descriptiveTextColor === ExtendedColorPalette.BLACK;

		const className = bemElement('report-block', 'descriptive-text', { 'black-text': blackText, 'bold-text': isDescriptiveTextBold });

		return (
			<div className={className}>
				{defaultValue}
			</div>
		);
	};

	renderImmutableUrl = () => {
		const { fieldName } = this.props;

		return (
			<ImmutableUrl
				fieldName={fieldName}
			/>
		);
	};

	renderField = () => {
		const { reportBlockField: { fieldType, unit } } = this.props;
		switch (fieldType) {
			case ReportBlockFieldEnum.Type.TIME:
				return this.renderTimeInputField();
			case ReportBlockFieldEnum.Type.IMAGE:
			case ReportBlockFieldEnum.Type.IMMUTABLE_IMAGE:
				return this.renderImageField();
			case ReportBlockFieldEnum.Type.BOOLEAN:
				return this.renderBooleanField();
			case ReportBlockFieldEnum.Type.ADDRESS:
				return this.renderAddressField();
			case ReportBlockFieldEnum.Type.CALCULATED:
				return this.renderCalculatedField();
			case ReportBlockFieldEnum.Type.SIGNATURE:
				return this.renderSignatureField();
			case ReportBlockFieldEnum.Type.IMMUTABLE_TEXT:
				return this.renderImmutableTextField();
			case ReportBlockFieldEnum.Type.INFORMATION:
				return this.renderTextAreaField();
			case ReportBlockFieldEnum.Type.DROPDOWN:
				return this.renderDropdownField();
			case ReportBlockFieldEnum.Type.EQUIPMENT:
				return this.renderEquipmentDropdownField();
			case ReportBlockFieldEnum.Type.LINE_BREAK:
				return <br />;
			case ReportBlockFieldEnum.Type.IMMUTABLE_URL:
				return this.renderImmutableUrl();
		}

		if (unit && CompoundUnitEnum[unit]) {
			return this.renderCompoundField();
		}

		return this.renderPlainField();
	};

	render() {
		const { fieldReportBlockField, reportBlockField, focusedBlockId, setFieldToFocus } = this.props;
		if (!fieldReportBlockField) {
			return null;
		}

		const { dimension, iconName } = reportBlockField;
		const isFocused = focusedBlockId === +fieldReportBlockField.id;

		const className = bemElement('field-report-block', 'field', {
			[dimension.toLowerCase()]: true,
			'with-icon': !!iconName,
			focused: isFocused,
		});

		return (
			<div className={className} ref={isFocused ? setFieldToFocus : undefined}>
				{iconName && <span className={`field-report-block__field__icon icon-${ReportBlockFieldIconName[iconName]}`} />}
				{this.renderField()}
			</div>
		);
	}
}

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { fieldReport: { fieldReport }, user: { userData } } = state;
	if (!userData) {
		throw new Error('User not logged in');
	}

	const { fieldId, fieldName } = ownProps;
	const fieldReportBlockField = fieldReport?.fieldReportBlockFieldMap?.[fieldId];
	if (!fieldReportBlockField) {
		throw new Error('fieldReportBlockField not found');
	}

	return {
		fieldReport: state.fieldReport.fieldReport,
		fieldReportBlockField,
		reportBlockField: fieldReport?.fieldMap?.[fieldReportBlockField?.reportBlockFieldId],
		formValue: formValueSelector(FIELD_REPORT)(state, fieldName),
		fullName: getUserName(userData),
	};
}

function mapDispatchToProps() {
	return {
		updateFieldValue: FieldReportActions.editFieldReportValue,
		deleteSignature: FieldReportActions.deleteBlockFieldSignature,
		syncFieldReportTypes: FieldReportActions.syncFieldReportTypes,
		fetchEquipmentList: FieldReportActions.fetchEquipmentList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(Field);
